import { SelectItem } from '../../types/SelectItem'
import { FormCode } from '@kistill/shared/types'

/**
 * Filter field transformation types
 */
export type FilterFieldTransform =
  | 'selectItemToIds' // Maps SelectItem[] to string[] (ids)
  | 'selectItemToIdsAsNumbers' // Maps SelectItem[] to number[] (ids as numbers)
  | 'selectItemToTitles' // Maps SelectItem[] to string[] (titles)
  | 'string' // Passes through as string
  | 'handleString' // Handles empty strings
  | 'radioGroup' // Radio group selection (empty string = undefined)
  | 'radioGroupToNumberArray' // Radio group selection converted to number[] (empty string = undefined)

/**
 * Filter field definition
 */
export interface FilterFieldDefinition {
  /** The key name in the filter params object */
  key: string
  /** The TypeScript type */
  type: 'SelectItem[] | null' | 'string | null'
  /** How to transform the field for the API */
  transform: FilterFieldTransform
  /** API field name (if different from key) */
  apiKey: string
  /** Options for radioGroup transform */
  options?: { value: string; label: string }[]
}

/**
 * All available filter fields - SINGLE SOURCE OF TRUTH
 * Each field is defined once here, and can be referenced by multiple filter types
 */
export const FILTER_FIELDS: Record<string, FilterFieldDefinition> = {
  // Common fields
  selectedGuids: {
    key: 'selectedGuids',
    type: 'SelectItem[] | null',
    transform: 'string',
    apiKey: 'selectedGuids',
  },
  showSelected: {
    key: 'showSelected',
    type: 'string | null',
    transform: 'string',
    apiKey: 'showSelected',
  },
  originNumberSelectItem: {
    key: 'originNumberSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToTitles',
    apiKey: 'originNumber',
  },
  dateFrom: {
    key: 'dateFrom',
    type: 'string | null',
    transform: 'string',
    apiKey: 'dateFrom',
  },
  dateTo: {
    key: 'dateTo',
    type: 'string | null',
    transform: 'string',
    apiKey: 'dateTo',
  },
  tengilidirSelectItem: {
    key: 'tengilidirSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIds',
    apiKey: 'tengilidir',
  },
  mottakendurSelectItem: {
    key: 'mottakendurSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIds',
    apiKey: 'mottakendur',
  },
  efnislysing: {
    key: 'efnislysing',
    type: 'string | null',
    transform: 'string',
    apiKey: 'efnislysing',
  },
  heiti: {
    key: 'heiti',
    type: 'string | null',
    transform: 'string',
    apiKey: 'heiti',
  },
  itemCollectionNumberSelectItem: {
    key: 'itemCollectionNumberSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToTitles',
    apiKey: 'itemCollectionNumber',
  },
  year: {
    key: 'year',
    type: 'string | null',
    transform: 'string',
    apiKey: 'year',
  },
  itemCollectionTypeSelectItem: {
    key: 'itemCollectionTypeSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIds',
    apiKey: 'itemCollectionTypes',
  },
  originalTitleSelectItem: {
    key: 'originalTitleSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToTitles',
    apiKey: 'originalTitle',
  },
  countriesSelectItem: {
    key: 'countriesSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIdsAsNumbers',
    apiKey: 'countries',
  },
  overClassificationSelectItem: {
    key: 'overClassificationSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIdsAsNumbers',
    apiKey: 'overClassification',
  },
  subClassificationSelectItem: {
    key: 'subClassificationSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIdsAsNumbers',
    apiKey: 'subClassification',
  },
  originalTitleType: {
    key: 'originalTitleType',
    type: 'string | null',
    transform: 'radioGroupToNumberArray',
    apiKey: 'originalTitleType',
  },
  source: {
    key: 'source',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIdsAsNumbers',
    apiKey: 'source',
  },
  locationInHouse: {
    key: 'locationInHouse',
    type: 'string | null',
    transform: 'string',
    apiKey: 'locationInHouse',
  },
  althjodlegurTitill: {
    key: 'althjodlegurTitill',
    type: 'string | null',
    transform: 'string',
    apiKey: 'althjodlegurTitill',
  },
  titleSelectItem: {
    key: 'titleSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToTitles',
    apiKey: 'title',
  },
  languagesSelectItem: {
    key: 'languagesSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIdsAsNumbers',
    apiKey: 'languages',
  },
  premiereDate: {
    key: 'premiereDate',
    type: 'string | null',
    transform: 'string',
    apiKey: 'premiereDate',
  },
  premiereLocation: {
    key: 'premiereLocation',
    type: 'string | null',
    transform: 'string',
    apiKey: 'premiereLocation',
  },
  creditFullyRegistered: {
    key: 'creditFullyRegistered',
    type: 'string | null',
    transform: 'string',
    apiKey: 'creditFullyRegistered',
  },
  comments: {
    key: 'comments',
    type: 'string | null',
    transform: 'string',
    apiKey: 'comments',
  },
  creditTypeSelectItem: {
    key: 'creditTypeSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIds',
    apiKey: 'creditType',
  },
  itemNumberSelectItem: {
    key: 'itemNumberSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToTitles',
    apiKey: 'itemNumber',
  },
  itemTypeSelectItem: {
    key: 'itemTypeSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIds',
    apiKey: 'itemTypes',
  },
  formSelectItem: {
    key: 'formSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIds',
    apiKey: 'formCodes',
  },
  stadaSkraningar: {
    key: 'stadaSkraningar',
    type: 'string | null',
    transform: 'string',
    apiKey: 'stadaSkraningar',
  },
  frumEintak: {
    key: 'frumEintak',
    type: 'string | null',
    transform: 'string',
    apiKey: 'frumEintak',
  },
  skilaskyltEintak: {
    key: 'skilaskyltEintak',
    type: 'string | null',
    transform: 'string',
    apiKey: 'skilaskyltEintak',
  },
  stafvaeding: {
    key: 'stafvaeding',
    type: 'string | null',
    transform: 'string',
    apiKey: 'stafvaeding',
  },
  locationsSelectItem: {
    key: 'locationsSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIds',
    apiKey: 'locations',
  },
  coldLocationsSelectItem: {
    key: 'coldLocationsSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIds',
    apiKey: 'coldLocations',
  },
  ownersNumber: {
    key: 'ownersNumber',
    type: 'string | null',
    transform: 'string',
    apiKey: 'ownersNumber',
  },
  breiddSelectItem: {
    key: 'breiddSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIdsAsNumbers',
    apiKey: 'breidd',
  },
  nedanmalstextarSelectItem: {
    key: 'nedanmalstextarSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIdsAsNumbers',
    apiKey: 'nedanmalstextarId',
  },
  rammarASekundu: {
    key: 'rammarASekundu',
    type: 'string | null',
    transform: 'string',
    apiKey: 'rammarASekundu',
  },
  gbStaerd: {
    key: 'gbStaerd',
    type: 'string | null',
    transform: 'string',
    apiKey: 'gbStaerd',
  },
  minnispunktar: {
    key: 'minnispunktar',
    type: 'string | null',
    transform: 'string',
    apiKey: 'minnispunktar',
  },
  lysingStafvaedingar: {
    key: 'lysingStafvaedingar',
    type: 'string | null',
    transform: 'string',
    apiKey: 'lysingStafvaedingar',
  },
  timabilEfnisFra: {
    key: 'timabilEfnisFra',
    type: 'string | null',
    transform: 'string',
    apiKey: 'timabilEfnisFra',
  },
  timabilEfnisTil: {
    key: 'timabilEfnisTil',
    type: 'string | null',
    transform: 'string',
    apiKey: 'timabilEfnisTil',
  },
  tagSelectItem: {
    key: 'tagSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIds',
    apiKey: 'tagsGuid',
  },
  skyring: {
    key: 'skyring',
    type: 'string | null',
    transform: 'string',
    apiKey: 'skyring',
  },
  athugasemd: {
    key: 'athugasemd',
    type: 'string | null',
    transform: 'string',
    apiKey: 'athugasemd',
  },
  lysing: {
    key: 'lysing',
    type: 'string | null',
    transform: 'string',
    apiKey: 'lysing',
  },
  staerd: {
    key: 'staerd',
    type: 'string | null',
    transform: 'string',
    apiKey: 'staerd',
  },
  buidAdFylla: {
    key: 'buidAdFylla',
    type: 'string | null',
    transform: 'radioGroup',
    apiKey: 'buidAdFylla',
    options: [
      { value: '', label: 'Öll' },
      { value: '1', label: 'Já' },
      { value: '0', label: 'Nei' },
    ],
  },
  ltoGerd: {
    key: 'ltoGerd',
    type: 'string | null',
    transform: 'radioGroup',
    apiKey: 'ltoGerd',
    options: [
      { value: '', label: 'Öll' },
      { value: 'A', label: 'A – frumrit' },
      { value: 'B', label: 'B – afrit' },
    ],
  },
  afritunLokid: {
    key: 'afritunLokid',
    type: 'string | null',
    transform: 'radioGroup',
    apiKey: 'afritunLokid',
    options: [
      { value: '', label: 'Öll' },
      { value: '1', label: 'Já' },
      { value: '0', label: 'Nei' },
    ],
  },
  slod: {
    key: 'slod',
    type: 'string | null',
    transform: 'string',
    apiKey: 'slod',
  },
  lysingSafnmunar: {
    key: 'lysingSafnmunar',
    type: 'string | null',
    transform: 'string',
    apiKey: 'lysingSafnmunar',
  },
  upplysingarUmSafnmuninn: {
    key: 'upplysingarUmSafnmuninn',
    type: 'string | null',
    transform: 'string',
    apiKey: 'upplysingarUmSafnmuninn',
  },
  heimildSafnmunir: {
    key: 'heimildSafnmunir',
    type: 'string | null',
    transform: 'string',
    apiKey: 'heimildSafnmunir',
  },
  upprunalegirEigendurSelectItem: {
    key: 'upprunalegirEigendurSelectItem',
    type: 'SelectItem[] | null',
    transform: 'selectItemToIds',
    apiKey: 'upprunalegirEigendurGuid',
  },
  itemOrderFrom: {
    key: 'itemOrderFrom',
    type: 'string | null',
    transform: 'handleString',
    apiKey: 'itemOrderFrom',
  },
  itemOrderTo: {
    key: 'itemOrderTo',
    type: 'string | null',
    transform: 'handleString',
    apiKey: 'itemOrderTo',
  },
}

/**
 * Column to filter field mapping
 * Maps column IDs to their corresponding filter field names
 * Used to hide/show filters based on column visibility
 */
export const COLUMN_TO_FILTER_MAP: Record<
  string,
  Record<string, string | string[]>
> = {
  origin: {
    originNumber: 'originNumberSelectItem',
    originName: 'heiti',
    tengilidir: 'tengilidirSelectItem',
    mottakendur: 'mottakendurSelectItem',
    lysing: 'efnislysing',
    dagsetning: ['dateFrom', 'dateTo'], // Date range uses two fields
  },
  itemCollection: {
    originNumber: 'originNumberSelectItem',
    itemCollectionNumber: 'itemCollectionNumberSelectItem',
    itemCollectionName: 'heiti',
    tegund: 'itemCollectionTypeSelectItem',
    lysing: 'efnislysing',
    year: 'year',
  },
  customer: {
    name: 'tengilidirSelectItem', // Filter uses tengilidirSelectItem but label is "Nafn aðila"
    identity: 'skyring',
    tags: 'tagSelectItem',
    comment: 'athugasemd',
    // Note: ssn, address, postalCode, phoneNumber, email don't have filters
  },
  credit: {
    titleName: 'titleSelectItem',
    customerName: 'tengilidirSelectItem', // Filter uses tengilidirSelectItem but label is "Nafn kredits"
    creditName: 'creditTypeSelectItem', // Filter uses creditTypeSelectItem but label is "Tegund kredits"
    comments: 'comments',
  },
  originalTitle: {
    originalTitleName: 'originalTitleSelectItem',
    althjodlegurTitill: 'althjodlegurTitill',
    upprunalond: 'countriesSelectItem',
    yfirflokkar: 'overClassificationSelectItem',
    undirflokkar: 'subClassificationSelectItem',
    heimild: 'source',
    stadsetningFrumefnisUtanhuss: 'locationInHouse',
    dagsetning: ['dateFrom', 'dateTo'],
    tegundTitils: 'originalTitleType',
    // Note: tegundTitilsHeiti doesn't have a filter
  },
  itemTitle: {
    titleName: 'titleSelectItem',
    tungumal: 'languagesSelectItem',
    year: 'year',
    premiereDate: 'premiereDate',
    premiereLocation: 'premiereLocation',
    comments: 'comments',
    creditFullyRegistered: 'creditFullyRegistered',
    // Note: timeLength doesn't have a filter
  },
  contentAnalysis: {
    itemNumber: 'itemNumberSelectItem',
    titleName: 'titleSelectItem',
    efnislysing: 'efnislysing',
    tags: 'tagSelectItem',
    year: ['dateFrom', 'dateTo'], // Uses date range
  },
  // Item types - these will be dynamically mapped based on FormCode
  // Note: Column IDs should match the column definitions in the list views
  KVIKS: {
    originNumber: 'originNumberSelectItem',
    itemCollectionNumber: 'itemCollectionNumberSelectItem',
    itemNumber: 'itemNumberSelectItem',
    itemTypeName: 'itemTypeSelectItem',
    title: 'titleSelectItem',
    stadsetningar: 'locationsSelectItem',
    ltoLocations: 'coldLocationsSelectItem',
    tungumal: 'languagesSelectItem',
    nedanmalstextar: 'nedanmalstextarSelectItem',
    rammarASekundu: 'rammarASekundu',
    gbStaerd: 'gbStaerd',
    athugasemdir: 'athugasemd',
    minnispunktar: 'minnispunktar',
    stadaSkraningar: 'stadaSkraningar',
    frumEintak: 'frumEintak',
    skilaskyltEintak: 'skilaskyltEintak',
    numerEiganda: 'ownersNumber',
  },
  KVIKH: {
    originNumber: 'originNumberSelectItem',
    itemCollectionNumber: 'itemCollectionNumberSelectItem',
    itemNumber: 'itemNumberSelectItem',
    title: 'titleSelectItem',
    stadsetningar: 'locationsSelectItem',
    languages: 'languagesSelectItem',
    stafvaeding: 'stafvaeding',
    numerEiganda: 'ownersNumber',
    breiddName: 'breiddSelectItem',
    athugasemdir: 'athugasemd',
    stadaSkraningar: 'stadaSkraningar',
    frumEintak: 'frumEintak',
    skilaskyltEintak: 'skilaskyltEintak',
    nedanmalstextar: 'nedanmalstextarSelectItem',
    lysingStafvaedingar: 'lysingStafvaedingar',
    minnispunktar: 'minnispunktar',
  },
  KVIK: {
    originNumber: 'originNumberSelectItem',
    itemCollectionNumber: 'itemCollectionNumberSelectItem',
    itemNumber: 'itemNumberSelectItem',
    itemTypeName: 'itemTypeSelectItem',
    title: 'titleSelectItem',
    languages: 'languagesSelectItem',
    nedanmalstextar: 'nedanmalstextarSelectItem',
    minnispunktar: 'minnispunktar',
    // Digital movie specific
    stadsetningar: 'locationsSelectItem',
    ltoLocations: 'coldLocationsSelectItem',
    rammarASekundu: 'rammarASekundu',
    gbStaerd: 'gbStaerd',
    athugasemdir: 'athugasemd',
    // Physical movie specific
    numerEiganda: 'ownersNumber',
    breiddName: 'breiddSelectItem',
    stafvaeding: 'stafvaeding',
    lysingStafvaedingar: 'lysingStafvaedingar',
    // Common status fields
    stadaSkraningar: 'stadaSkraningar',
    frumEintak: 'frumEintak',
    skilaskyltEintak: 'skilaskyltEintak',
  },
  GOGN: {
    originNumber: 'originNumberSelectItem',
    itemCollectionNumber: 'itemCollectionNumberSelectItem',
    itemNumber: 'itemNumberSelectItem',
    title: 'titleSelectItem',
    itemTypeName: 'itemTypeSelectItem',
    locations: 'locationsSelectItem',
    itemName: 'heiti',
    timabilEfnisFra: 'timabilEfnisFra',
    timabilEfnisTil: 'timabilEfnisTil',
    athugasemdir: 'athugasemd',
    minnispunktar: 'minnispunktar',
    stadaSkraningar: 'stadaSkraningar',
    frumEintak: 'frumEintak',
    skilaskyltEintak: 'skilaskyltEintak',
    originYear: 'year',
  },
  GOGNGR: {
    originNumber: 'originNumberSelectItem',
    itemCollectionNumber: 'itemCollectionNumberSelectItem',
    itemNumber: 'itemNumberSelectItem',
    title: 'titleSelectItem',
    itemTypeName: 'itemTypeSelectItem',
    locations: 'locationsSelectItem',
    ltoLocations: 'coldLocationsSelectItem',
    itemName: 'heiti',
    timabilEfnisFra: 'timabilEfnisFra',
    timabilEfnisTil: 'timabilEfnisTil',
    athugasemdir: 'athugasemd',
    minnispunktar: 'minnispunktar',
    stadaSkraningar: 'stadaSkraningar',
    frumEintak: 'frumEintak',
    skilaskyltEintak: 'skilaskyltEintak',
    originYear: 'year',
  },
  LTOB: {
    originNumber: 'originNumberSelectItem',
    itemCollectionNumber: 'itemCollectionNumberSelectItem',
    itemNumber: 'itemNumberSelectItem',
    itemTypeName: 'itemTypeSelectItem',
    itemName: 'heiti',
    lysing: 'lysing',
    athugasemdir: 'athugasemd',
    staerd: 'staerd',
    buidAdFylla: 'buidAdFylla',
    ltoGerd: 'ltoGerd',
  },
  DISK: {
    originNumber: 'originNumberSelectItem',
    itemCollectionNumber: 'itemCollectionNumberSelectItem',
    itemNumber: 'itemNumberSelectItem',
    itemName: 'heiti',
    lysing: 'lysing',
    athugasemdir: 'athugasemd',
    staerd: 'staerd',
    afritunLokid: 'afritunLokid',
    itemTypeName: 'itemTypeSelectItem',
  },
  SAFNMUN: {
    originNumber: 'originNumberSelectItem',
    itemCollectionNumber: 'itemCollectionNumberSelectItem',
    itemNumber: 'itemNumberSelectItem',
    itemName: 'heiti',
    title: 'titleSelectItem',
    itemTypeName: 'itemTypeSelectItem',
    locations: 'locationsSelectItem',
    upprunalegirEigendur: 'upprunalegirEigendurSelectItem',
    slod: 'slod',
    athugasemdir: 'athugasemd',
    minnispunktar: 'minnispunktar',
    lysingSafnmunar: 'lysingSafnmunar',
    upplysingarUmSafnmuninn: 'upplysingarUmSafnmuninn',
    heimildSafnmunir: 'heimildSafnmunir',
    stadaSkraningar: 'stadaSkraningar',
    frumEintak: 'frumEintak',
    skilaskyltEintak: 'skilaskyltEintak',
  },
  // Generic "items" type - includes ALL fields from ALL item types (canonical order)
  items: {
    originNumber: 'originNumberSelectItem',
    itemCollectionNumber: 'itemCollectionNumberSelectItem',
    itemNumber: 'itemNumberSelectItem',
    formName: 'formSelectItem',
    itemTypeName: 'itemTypeSelectItem',
    itemName: 'heiti',
    title: 'titleSelectItem',
    stadsetningar: 'locationsSelectItem',
    ltoLocations: 'coldLocationsSelectItem',
    tungumal: 'languagesSelectItem',
    nedanmalstextar: 'nedanmalstextarSelectItem',
    numerEiganda: 'ownersNumber',
    breiddName: 'breiddSelectItem',
    rammarASekundu: 'rammarASekundu',
    gbStaerd: 'gbStaerd',
    staerd: 'staerd',
    slod: 'slod',
    upprunalegirEigendur: 'upprunalegirEigendurSelectItem',
    lysing: 'lysing',
    athugasemdir: 'athugasemd',
    minnispunktar: 'minnispunktar',
    lysingStafvaedingar: 'lysingStafvaedingar',
    lysingSafnmunar: 'lysingSafnmunar',
    upplysingarUmSafnmuninn: 'upplysingarUmSafnmuninn',
    heimildSafnmunir: 'heimildSafnmunir',
    timabilEfnisFra: 'timabilEfnisFra',
    timabilEfnisTil: 'timabilEfnisTil',
    originYear: 'year',
    afritunLokid: 'afritunLokid',
    buidAdFylla: 'buidAdFylla',
    ltoGerd: 'ltoGerd',
    stadaSkraningar: 'stadaSkraningar',
    stafvaeding: 'stafvaeding',
    frumEintak: 'frumEintak',
    skilaskyltEintak: 'skilaskyltEintak',
  },
}

/**
 * Get filter field names that should be hidden based on column visibility
 * A filter is hidden only when EVERY column that maps to it is hidden.
 * (So if "Staðsetningar" and "locations" both map to locationsSelectItem,
 * we show the filter when at least one of those columns is visible.)
 * @param type - The filter type (e.g., 'origin', 'itemCollection')
 * @param columnVisibility - Object mapping column IDs to their visibility state
 * @returns Set of filter field names that should be hidden
 */
export const getHiddenFilterFields = (
  type: string,
  columnVisibility: { [key: string]: boolean } = {},
): Set<string> => {
  const hiddenFields = new Set<string>()
  const columnMap = COLUMN_TO_FILTER_MAP[type]

  if (!columnMap) return hiddenFields

  // Build reverse map: filterField -> columnIds that map to it
  const filterToColumns: Record<string, string[]> = {}
  Object.entries(columnMap).forEach(([columnId, filterField]) => {
    const fields = Array.isArray(filterField) ? filterField : [filterField]
    fields.forEach((field) => {
      if (!filterToColumns[field]) filterToColumns[field] = []
      filterToColumns[field].push(columnId)
    })
  })

  // Hide a filter only if EVERY column that maps to it is hidden
  Object.entries(filterToColumns).forEach(([filterField, columnIds]) => {
    const allHidden = columnIds.every(
      (colId) => columnVisibility[colId] === false,
    )
    if (allHidden) {
      hiddenFields.add(filterField)
    }
  })

  return hiddenFields
}

/**
 * Get filter fields that should be visually reset when columns transition from hidden to visible.
 * RHF retains field values for unmounted fields (shouldUnregister: false), so when a column
 * is re-shown its field may display a stale value even though the filter was cleared on hide.
 * @param type - The filter type (e.g., 'origin', 'itemCollection')
 * @param columnVisibility - Current column visibility state
 * @param prevColumnVisibility - Previous column visibility state
 * @returns Array of filter field names that should be reset to null
 */
export const getFieldsToClearOnShow = (
  type: string,
  columnVisibility: { [key: string]: boolean } = {},
  prevColumnVisibility: { [key: string]: boolean } = {},
): string[] => {
  const fieldsToReset: string[] = []
  const columnMap = COLUMN_TO_FILTER_MAP[type]

  if (!columnMap) return fieldsToReset

  Object.entries(columnMap).forEach(([columnId, filterField]) => {
    const wasHidden = prevColumnVisibility[columnId] === false
    const isNowVisible = columnVisibility[columnId] !== false

    if (wasHidden && isNowVisible) {
      if (Array.isArray(filterField)) {
        fieldsToReset.push(...filterField)
      } else {
        fieldsToReset.push(filterField)
      }
    }
  })

  return fieldsToReset
}

/**
 * Get filter fields that should be cleared when columns transition from visible to hidden
 * @param type - The filter type (e.g., 'origin', 'itemCollection')
 * @param columnVisibility - Current column visibility state
 * @param prevColumnVisibility - Previous column visibility state
 * @returns Array of filter field names that should be cleared
 */
export const getFieldsToClearOnHide = (
  type: string,
  columnVisibility: { [key: string]: boolean } = {},
  prevColumnVisibility: { [key: string]: boolean } = {},
): string[] => {
  const fieldsToClear: string[] = []
  const columnMap = COLUMN_TO_FILTER_MAP[type]

  if (!columnMap) return fieldsToClear

  Object.entries(columnMap).forEach(([columnId, filterField]) => {
    // Check if column transitioned from visible to hidden
    const wasVisible = prevColumnVisibility[columnId] !== false
    const isNowHidden = columnVisibility[columnId] === false

    if (wasVisible && isNowHidden) {
      if (Array.isArray(filterField)) {
        fieldsToClear.push(...filterField)
      } else {
        fieldsToClear.push(filterField)
      }
    }
  })

  return fieldsToClear
}

/**
 * Filter type configurations
 * Each type references which fields it uses from FILTER_FIELDS
 * Fields can be shared across multiple types
 */
export const FILTER_TYPE_CONFIGS = {
  origin: [
    'originNumberSelectItem',
    'tengilidirSelectItem',
    'mottakendurSelectItem',
    'efnislysing',
    'dateFrom',
    'dateTo',
    'heiti',
  ] as const,
  itemCollection: [
    'originNumberSelectItem',
    'itemCollectionNumberSelectItem',
    'itemCollectionTypeSelectItem',
    'year',
    'efnislysing',
    'heiti',
  ] as const,
  originalTitle: [
    'originalTitleSelectItem',
    'althjodlegurTitill',
    'countriesSelectItem',
    'overClassificationSelectItem',
    'subClassificationSelectItem',
    'dateFrom',
    'dateTo',
    'originalTitleType',
    'source',
    'locationInHouse',
  ] as const,
  itemTitle: [
    'titleSelectItem',
    'dateFrom',
    'dateTo',
    'languagesSelectItem',
    'year',
    'premiereDate',
    'premiereLocation',
    'creditFullyRegistered',
    'comments',
  ] as const,
  credit: [
    'titleSelectItem',
    'tengilidirSelectItem',
    'creditTypeSelectItem',
    'comments',
  ] as const,
  customer: [
    'tengilidirSelectItem',
    'tagSelectItem',
    'skyring',
    'athugasemd',
  ] as const,
  connectedItems: [
    'itemNumberSelectItem',
    'itemCollectionNumberSelectItem',
    'itemTypeSelectItem',
    'itemOrderFrom',
    'itemOrderTo',
  ] as const,
  itemLocations: [
    'itemNumberSelectItem',
    'itemCollectionNumberSelectItem',
    'itemOrderFrom',
    'itemOrderTo',
  ] as const,
  contentAnalysis: [
    'itemNumberSelectItem',
    'titleSelectItem',
    'efnislysing',
    'tagSelectItem',
    'dateFrom',
    'dateTo',
  ] as const,
  // Generic "items" type - includes ALL fields from ALL item types (canonical order)
  items: [
    'originNumberSelectItem',
    'itemCollectionNumberSelectItem',
    'itemNumberSelectItem',
    'formSelectItem',
    'itemTypeSelectItem',
    'heiti',
    'titleSelectItem',
    'locationsSelectItem',
    'coldLocationsSelectItem',
    'languagesSelectItem',
    'nedanmalstextarSelectItem',
    'ownersNumber',
    'breiddSelectItem',
    'rammarASekundu',
    'gbStaerd',
    'staerd',
    'slod',
    'upprunalegirEigendurSelectItem',
    'lysing',
    'athugasemd',
    'minnispunktar',
    'lysingStafvaedingar',
    'lysingSafnmunar',
    'upplysingarUmSafnmuninn',
    'heimildSafnmunir',
    'timabilEfnisFra',
    'timabilEfnisTil',
    'year',
    'afritunLokid',
    'buidAdFylla',
    'ltoGerd',
    'stadaSkraningar',
    'stafvaeding',
    'frumEintak',
    'skilaskyltEintak',
  ] as const,
  // Item subtypes (FormCode values) - each has specific fields
  itemSubtypes: {
    // Default filters only: númer uppruna, númer aðfangs, númer eintaks. No stada/frum/skilaskylt by default.
    baseFields: [
      'originNumberSelectItem',
      'itemCollectionNumberSelectItem',
      'itemNumberSelectItem',
    ] as const,
    // Subtype-specific fields
    subtypes: {
      // KVIKS - Kvikmyndir stafrænar (Digital Movies)
      KVIKS: [
        'itemTypeSelectItem',
        'titleSelectItem',
        'locationsSelectItem',
        'coldLocationsSelectItem',
        'languagesSelectItem',
        'nedanmalstextarSelectItem',
        'rammarASekundu',
        'gbStaerd',
        'athugasemd',
        'minnispunktar',
        'stadaSkraningar',
        'frumEintak',
        'skilaskyltEintak',
      ] as const,
      // KVIKH - Kvikmyndir hliðrænar (Physical Movies)
      KVIKH: [
        'itemTypeSelectItem',
        'titleSelectItem',
        'locationsSelectItem',
        'languagesSelectItem',
        'ownersNumber',
        'breiddSelectItem',
        'stafvaeding',
        'nedanmalstextarSelectItem',
        'lysingStafvaedingar',
        'minnispunktar',
        'stadaSkraningar',
        'frumEintak',
        'skilaskyltEintak',
      ] as const,
      // KVIK - Kvikmyndir (All Movies) - includes all fields from both digital and physical
      // Pre-selected: only common fields between KVIKS and KVIKH
      // All fields available: union of KVIKS and KVIKH fields
      KVIK: [
        // Common fields (pre-selected by default)
        'itemTypeSelectItem',
        'titleSelectItem',
        'languagesSelectItem',
        'nedanmalstextarSelectItem',
        'minnispunktar',
        // Digital movie specific (available but not pre-selected)
        'locationsSelectItem',
        'coldLocationsSelectItem',
        'rammarASekundu',
        'gbStaerd',
        'athugasemd',
        // Physical movie specific (available but not pre-selected)
        'ownersNumber',
        'breiddSelectItem',
        'stafvaeding',
        'lysingStafvaedingar',
        'stadaSkraningar',
        'frumEintak',
        'skilaskyltEintak',
      ] as const,
      // GOGN - Gögn (Data)
      GOGN: [
        'titleSelectItem',
        'itemTypeSelectItem',
        'locationsSelectItem',
        'heiti',
        'athugasemd',
        'minnispunktar',
        'timabilEfnisFra',
        'timabilEfnisTil',
        'year',
        'stadaSkraningar',
        'frumEintak',
        'skilaskyltEintak',
      ] as const,
      // GOGNGR - Gögn stafræn (Digital Data)
      GOGNGR: [
        'titleSelectItem',
        'itemTypeSelectItem',
        'locationsSelectItem',
        'coldLocationsSelectItem',
        'heiti',
        'athugasemd',
        'minnispunktar',
        'timabilEfnisFra',
        'timabilEfnisTil',
        'year',
        'stadaSkraningar',
        'frumEintak',
        'skilaskyltEintak',
      ] as const,
      // SAFNMUN - Safnmunur
      SAFNMUN: [
        'titleSelectItem',
        'itemTypeSelectItem',
        'locationsSelectItem',
        'upprunalegirEigendurSelectItem',
        'heiti',
        'slod',
        'athugasemd',
        'minnispunktar',
        'lysingSafnmunar',
        'upplysingarUmSafnmuninn',
        'heimildSafnmunir',
        'stadaSkraningar',
        'frumEintak',
        'skilaskyltEintak',
      ] as const,
      // LTOB - LTO bönd
      LTOB: [
        'itemNumberSelectItem',
        'itemTypeSelectItem',
        'heiti',
        'lysing',
        'athugasemd',
        'staerd',
        'buidAdFylla',
        'ltoGerd',
      ] as const,
      // DISK - Diskar
      DISK: [
        'heiti',
        'lysing',
        'athugasemd',
        'staerd',
        'afritunLokid',
        'itemTypeSelectItem',
      ] as const,
    },
  },
} as const

/**
 * Get all fields for a given filter type (including subtypes for items)
 */
export const getFieldsForType = (
  type: string | undefined,
): readonly string[] => {
  if (!type) return []

  // Check if it's a FormCode type (item subtype)
  if (Object.values(FormCode).includes(type as FormCode)) {
    const itemSubtypes = FILTER_TYPE_CONFIGS.itemSubtypes
    const baseFields = itemSubtypes.baseFields

    // Check if this FormCode has subtype-specific fields
    const subtypeFields =
      itemSubtypes.subtypes[type as keyof typeof itemSubtypes.subtypes]
    if (subtypeFields) {
      return [...baseFields, ...subtypeFields]
    }

    return baseFields
  }

  // Check direct type match
  const config = FILTER_TYPE_CONFIGS[type as keyof typeof FILTER_TYPE_CONFIGS]
  if (!config) return []

  // If it's an array (simple type), return it
  if (Array.isArray(config)) {
    return config
  }

  return []
}

/**
 * Get subtype-specific fields for a given FormCode
 * Returns empty array if not a FormCode or no subtype fields defined
 */
export const getSubtypeFields = (
  type: string | undefined,
): readonly string[] => {
  if (!type) return []

  // Check if it's a FormCode type
  if (Object.values(FormCode).includes(type as FormCode)) {
    const itemSubtypes = FILTER_TYPE_CONFIGS.itemSubtypes
    const subtypeFields =
      itemSubtypes.subtypes[type as keyof typeof itemSubtypes.subtypes]
    return subtypeFields || []
  }

  return []
}

/**
 * Generate TypeScript type for filter parameters based on fields
 */
export type FilterParams = {
  [K in keyof typeof FILTER_FIELDS]?: (typeof FILTER_FIELDS)[K]['type'] extends 'SelectItem[] | null'
    ? SelectItem[] | null
    : string | null
} & {
  selectedGuids?: [] | null
  showSelected?: string
}

/**
 * Returns true if params are effectively empty (no active filter).
 * Clear saves params with all keys set to null/empty, not literally {}.
 */
export function isEmptyFilterParams(
  params: Record<string, unknown> | null | undefined,
): boolean {
  if (params === null || params === undefined) return true
  for (const v of Object.values(params)) {
    if (v === null || v === undefined || v === '') continue
    if (v === false) continue
    if (Array.isArray(v) && v.length === 0) continue
    if (
      typeof v === 'object' &&
      !Array.isArray(v) &&
      Object.keys(v as object).length === 0
    )
      continue
    return false
  }
  return true
}

/**
 * Get all field keys in the order they appear in FILTER_FIELDS
 * This is used to maintain consistent ordering across all usages
 *
 * IMPORTANT: The order returned by this function MUST match the parameter order
 * in OnFilterType in useHandleOnSubmitFilters.ts
 *
 * When adding a new field to FILTER_FIELDS:
 * 1. Add it to FILTER_FIELDS (this function will automatically include it)
 * 2. Add it to OnFilterType in the same position as it appears here
 * 3. Add it to the relevant FILTER_TYPE_CONFIGS
 */
export const getAllFieldKeys = (): string[] => {
  // Return keys in the exact order they appear in FILTER_FIELDS
  // This ensures consistency with OnFilterType parameter order
  const keys: string[] = []
  for (const key in FILTER_FIELDS) {
    if (key !== 'showSelected' && key !== 'selectedGuids') {
      keys.push(key)
    }
  }
  return keys
}

/**
 * Generate function parameter types from FILTER_FIELDS
 * This creates a tuple type that matches the order of getAllFieldKeys()
 */
type FilterFieldParam<K extends string> =
  (typeof FILTER_FIELDS)[K]['type'] extends 'SelectItem[] | null'
    ? SelectItem[] | null
    : string | null

/**
 * OnFilterType - Function signature that accepts all possible filter fields
 * Generated from FILTER_FIELDS to ensure single source of truth
 *
 * NOTE: The parameter order MUST match getAllFieldKeys() from filterConfig.ts
 * When adding new fields, add them here in the same order as in FILTER_FIELDS
 */
export type OnFilterType = (
  showSelected: string,
  selectedGuids?: [] | null,
  // Order matches getAllFieldKeys() from filterConfig.ts
  originNumberSelectItem?: FilterFieldParam<'originNumberSelectItem'>,
  dateFrom?: FilterFieldParam<'dateFrom'>,
  dateTo?: FilterFieldParam<'dateTo'>,
  tengilidirSelectItem?: FilterFieldParam<'tengilidirSelectItem'>,
  mottakendurSelectItem?: FilterFieldParam<'mottakendurSelectItem'>,
  efnislysing?: FilterFieldParam<'efnislysing'>,
  heiti?: FilterFieldParam<'heiti'>,
  itemCollectionNumberSelectItem?: FilterFieldParam<'itemCollectionNumberSelectItem'>,
  year?: FilterFieldParam<'year'>,
  itemCollectionTypeSelectItem?: FilterFieldParam<'itemCollectionTypeSelectItem'>,
  originalTitleSelectItem?: FilterFieldParam<'originalTitleSelectItem'>,
  countriesSelectItem?: FilterFieldParam<'countriesSelectItem'>,
  overClassificationSelectItem?: FilterFieldParam<'overClassificationSelectItem'>,
  subClassificationSelectItem?: FilterFieldParam<'subClassificationSelectItem'>,
  originalTitleType?: FilterFieldParam<'originalTitleType'>,
  source?: FilterFieldParam<'source'>,
  locationInHouse?: FilterFieldParam<'locationInHouse'>,
  althjodlegurTitill?: FilterFieldParam<'althjodlegurTitill'>,
  titleSelectItem?: FilterFieldParam<'titleSelectItem'>,
  languagesSelectItem?: FilterFieldParam<'languagesSelectItem'>,
  premiereDate?: FilterFieldParam<'premiereDate'>,
  premiereLocation?: FilterFieldParam<'premiereLocation'>,
  creditFullyRegistered?: FilterFieldParam<'creditFullyRegistered'>,
  comments?: FilterFieldParam<'comments'>,
  creditTypeSelectItem?: FilterFieldParam<'creditTypeSelectItem'>,
  itemNumberSelectItem?: FilterFieldParam<'itemNumberSelectItem'>,
  itemTypeSelectItem?: FilterFieldParam<'itemTypeSelectItem'>,
  formSelectItem?: FilterFieldParam<'formSelectItem'>,
  stadaSkraningar?: FilterFieldParam<'stadaSkraningar'>,
  frumEintak?: FilterFieldParam<'frumEintak'>,
  skilaskyltEintak?: FilterFieldParam<'skilaskyltEintak'>,
  stafvaeding?: FilterFieldParam<'stafvaeding'>,
  locationsSelectItem?: FilterFieldParam<'locationsSelectItem'>,
  coldLocationsSelectItem?: FilterFieldParam<'coldLocationsSelectItem'>,
  ownersNumber?: FilterFieldParam<'ownersNumber'>,
  breiddSelectItem?: FilterFieldParam<'breiddSelectItem'>,
  nedanmalstextarSelectItem?: FilterFieldParam<'nedanmalstextarSelectItem'>,
  rammarASekundu?: FilterFieldParam<'rammarASekundu'>,
  gbStaerd?: FilterFieldParam<'gbStaerd'>,
  minnispunktar?: FilterFieldParam<'minnispunktar'>,
  lysingStafvaedingar?: FilterFieldParam<'lysingStafvaedingar'>,
  timabilEfnisFra?: FilterFieldParam<'timabilEfnisFra'>,
  timabilEfnisTil?: FilterFieldParam<'timabilEfnisTil'>,
  tagSelectItem?: FilterFieldParam<'tagSelectItem'>,
  skyring?: FilterFieldParam<'skyring'>,
  athugasemd?: FilterFieldParam<'athugasemd'>,
  lysing?: FilterFieldParam<'lysing'>,
  staerd?: FilterFieldParam<'staerd'>,
  buidAdFylla?: FilterFieldParam<'buidAdFylla'>,
  ltoGerd?: FilterFieldParam<'ltoGerd'>,
  afritunLokid?: FilterFieldParam<'afritunLokid'>,
  slod?: FilterFieldParam<'slod'>,
  lysingSafnmunar?: FilterFieldParam<'lysingSafnmunar'>,
  upplysingarUmSafnmuninn?: FilterFieldParam<'upplysingarUmSafnmuninn'>,
  heimildSafnmunir?: FilterFieldParam<'heimildSafnmunir'>,
  upprunalegirEigendurSelectItem?: FilterFieldParam<'upprunalegirEigendurSelectItem'>,
  itemOrderFrom?: FilterFieldParam<'itemOrderFrom'>,
  itemOrderTo?: FilterFieldParam<'itemOrderTo'>,
) => void
